import { useId, useMemo, useRef, useState } from "react";
import type { Faction, Relationship } from "../types";
import { gsap, useGSAP } from "../lib/animation";

interface FactionGraphProps {
  dragonName: string;
  factions: Faction[];
  relationships: Relationship[];
  accent: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onMove: (id: string, x: number, y: number) => void;
}

function safeId(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, "-");
}

function factionInitials(name: string): string {
  const words = name
    .replace(/[^a-zA-Z0-9\s-]/g, " ")
    .split(/[\s-]+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}

export function FactionGraph({
  dragonName,
  factions,
  relationships,
  accent,
  selectedId,
  onSelect,
  onMove
}: FactionGraphProps) {
  const [dragged, setDragged] = useState<string | null>(null);
  const graphRef = useRef<SVGSVGElement | null>(null);
  const id = useId().replace(/:/g, "");
  const lookup = useMemo(() => new Map(factions.map((faction) => [faction.id, faction])), [factions]);

  useGSAP(
    () => {
      const root = graphRef.current;
      if (!root || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      const edges = gsap.utils.toArray<SVGPathElement>(".faction-edge", root);
      const nodes = gsap.utils.toArray<SVGGElement>(".faction-node", root);

      edges.forEach((edge, index) => {
        const length = edge.getTotalLength();
        gsap.fromTo(
          edge,
          { strokeDasharray: length, strokeDashoffset: length, autoAlpha: 0.2 },
          {
            strokeDashoffset: 0,
            autoAlpha: 0.88,
            duration: 0.72 + index * 0.04,
            ease: "power2.out",
            clearProps: "strokeDasharray,strokeDashoffset,visibility,opacity"
          }
        );
      });

      gsap.from(nodes, {
        autoAlpha: 0,
        scale: 0.74,
        transformOrigin: "center center",
        stagger: { amount: 0.28, from: "center" },
        duration: 0.5,
        ease: "back.out(1.4)"
      });
    },
    {
      scope: graphRef,
      dependencies: [dragonName, factions.length, relationships.length],
      revertOnUpdate: true
    }
  );

  useGSAP(
    () => {
      const root = graphRef.current;
      if (!root || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const selected = root.querySelector(".faction-node.is-selected");
      if (!selected) return;
      gsap.fromTo(
        selected,
        { scale: 0.92, transformOrigin: "center center" },
        {
          scale: 1.08,
          repeat: 1,
          yoyo: true,
          duration: 0.22,
          ease: "sine.inOut",
          clearProps: "scale"
        }
      );
    },
    { scope: graphRef, dependencies: [selectedId] }
  );

  const updatePosition = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!dragged) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    onMove(
      dragged,
      Math.min(93, Math.max(7, ((event.clientX - bounds.left) / bounds.width) * 100)),
      Math.min(92, Math.max(8, ((event.clientY - bounds.top) / bounds.height) * 100))
    );
  };
  return (
    <svg
      ref={graphRef}
      className="faction-graph"
      viewBox="0 0 100 100"
      role="img"
      aria-label={`Faction relationships around ${dragonName}`}
      onPointerMove={updatePosition}
      onPointerUp={() => setDragged(null)}
      onPointerLeave={() => setDragged(null)}
    >
      <defs>
        <radialGradient id={`${id}-faction-center`}>
          <stop offset="0" stopColor={accent} stopOpacity=".8" />
          <stop offset="1" stopColor="#1a100c" />
        </radialGradient>
        <filter id={`${id}-faction-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="0" stdDeviation="1.2" floodColor={accent} floodOpacity=".48" />
          <feDropShadow dx="0" dy=".6" stdDeviation=".45" floodColor="#050403" floodOpacity=".86" />
        </filter>
        <filter id={`${id}-seal-grain`} x="-20%" y="-20%" width="140%" height="140%">
          <feTurbulence type="fractalNoise" baseFrequency=".82" numOctaves="2" seed="11" result="grain" />
          <feColorMatrix
            in="grain"
            type="matrix"
            values="0 0 0 0 0.9 0 0 0 0 0.72 0 0 0 0 0.38 0 0 0 .18 0"
            result="warmGrain"
          />
          <feComposite in="warmGrain" in2="SourceAlpha" operator="in" result="maskedGrain" />
          <feBlend in="SourceGraphic" in2="maskedGrain" mode="soft-light" />
        </filter>
        {factions.map((faction) => {
          const factionId = safeId(faction.id);
          return (
            <g key={`${faction.id}-defs`}>
              <radialGradient id={`${id}-seal-${factionId}`} cx="38%" cy="26%" r="72%">
                <stop offset="0" stopColor="#fff1bf" stopOpacity=".8" />
                <stop offset=".24" stopColor={faction.color} stopOpacity=".92" />
                <stop offset=".72" stopColor="#14110d" stopOpacity=".98" />
                <stop offset="1" stopColor="#050403" />
              </radialGradient>
              <linearGradient id={`${id}-rim-${factionId}`} x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#fff0b0" stopOpacity=".74" />
                <stop offset=".36" stopColor={faction.color} stopOpacity=".76" />
                <stop offset=".68" stopColor="#21160c" stopOpacity=".95" />
                <stop offset="1" stopColor="#f0cb79" stopOpacity=".62" />
              </linearGradient>
            </g>
          );
        })}
      </defs>
      <g className="faction-orbits" aria-hidden="true">
        <circle cx="50" cy="50" r="21" />
        <circle cx="50" cy="50" r="34" />
        <circle cx="50" cy="50" r="45" />
      </g>
      {relationships.map((relationship) => {
        const faction =
          relationship.source === "dragon"
            ? lookup.get(relationship.target)
            : lookup.get(relationship.source);
        if (!faction) return null;
        const path = `M50 50 Q${50 + (faction.y - 50) * 0.12} ${50 + (50 - faction.x) * 0.12} ${faction.x} ${faction.y}`;
        const selectedEdge = selectedId === faction.id;
        return (
          <g key={relationship.id}>
            <path className="faction-edge-shadow" d={path} />
            <path
              className={`faction-edge-energy ${selectedEdge ? "is-active" : ""}`}
              d={path}
              fill="none"
              stroke={faction.color}
            />
            <path
              className={`faction-edge faction-edge--${relationship.type} ${
                selectedEdge ? "is-active" : ""
              }`}
              d={path}
              fill="none"
              stroke={faction.color}
              strokeWidth=".75"
              strokeDasharray={relationship.type === "spies" ? "2 1.5" : undefined}
              opacity=".85"
            />
          </g>
        );
      })}
      <g className="faction-center" transform="translate(50 50)">
        <circle className="faction-center__aura" r="18" fill={accent} />
        <path className="faction-center__halo-lines" d="M-22 0H-16M16 0H22M0-22V-16M0 16V22" />
        <circle className="faction-center__ring" r="14.8" />
        <circle r="12.2" fill={`url(#${id}-faction-center)`} stroke={accent} strokeWidth=".8" filter={`url(#${id}-faction-glow)`} />
        <text className="faction-center__monogram" y="1.9" textAnchor="middle">
          {factionInitials(dragonName)}
        </text>
        <text className="faction-center__title" y="16" textAnchor="middle">
          {dragonName}
        </text>
      </g>
      {factions.map((faction) => {
        const selected = selectedId === faction.id;
        const labelOffset = faction.x < 18 ? 7 : faction.x > 82 ? -7 : 0;
        const shortName = faction.name.split(" ").slice(0, 2).join(" ");
        const factionId = safeId(faction.id);
        const initials = factionInitials(faction.name);
        const sealRadius = selected ? 9.7 : 8.9;
        const discRadius = selected ? 7.15 : 6.55;
        return (
          <g
            key={faction.id}
            transform={`translate(${faction.x} ${faction.y})`}
            className={`faction-node ${selected ? "is-selected" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={`${faction.name}, ${faction.relation}`}
            onClick={() => onSelect(faction.id)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") onSelect(faction.id);
            }}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              setDragged(faction.id);
              onSelect(faction.id);
            }}
          >
            <title>{`${faction.name} - ${faction.kind} - ${faction.relation}`}</title>
            <desc>{faction.goal}</desc>
            <circle className="faction-node__focus" r={selected ? 13.5 : 11.8} />
            <circle className="faction-node__aura" r={selected ? 11.4 : 10.2} fill={faction.color} />
            <g className="faction-node__seal">
              <circle className="faction-node__seal-shadow" r={sealRadius + 0.75} />
              <circle
                className="faction-node__rim"
                r={sealRadius}
                fill={`url(#${id}-rim-${factionId})`}
                stroke={faction.color}
              />
              <circle
                className="faction-node__disc"
                r={discRadius}
                fill={`url(#${id}-seal-${factionId})`}
                filter={`url(#${id}-seal-grain)`}
              />
              <ellipse className="faction-node__sheen" cx="-2.2" cy="-3" rx="3.8" ry="2.1" />
              <path className="faction-node__facet" d="M-5.1-1.5C-2.4-4.5 2.9-4.8 5.3-1.2M-5 1.7C-2.1 4.5 2.7 4.2 5.1 1.5" />
              <text className="faction-node__monogram" y=".85" textAnchor="middle">
                {initials}
              </text>
              <text className="faction-node__power" y="5.18" textAnchor="middle">
                {faction.power}
              </text>
            </g>
            <g className="faction-node__label" transform={`translate(${labelOffset} 12.2)`}>
              <rect x="-12.8" y="-3" width="25.6" height="6.2" rx="1.8" />
              <text y="-0.55" textAnchor="middle">
                {shortName}
              </text>
              <text y="2.05" textAnchor="middle" className="faction-node__relation">
                {faction.relation}
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}
